import { HubConnectionState } from '@microsoft/signalr';
import { useCallback, useState } from 'react';
import { BulkEditStatus } from 'src/components/common/BulkActions/BulkEditStatus';
import { BulkEditPreviewListingDetails } from 'src/components/common/BulkActions/BulkEditStatus/components/BulkEditPreviewDisplay/BulkEditPreviewListingDetails';
import { SectionSelector } from 'src/components/Selectors/SectionRowSelector';
import {
  BulkEditStage,
  useBulkEditHubContext,
} from 'src/contexts/BulkEditHubContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useGetEventMapConfig } from 'src/contexts/EventMapContext';
import { Checkbox } from 'src/core/interim/Checkbox';
import { GenericDialog } from 'src/core/interim/dialogs/GenericDialog';
import { shared, vars } from 'src/core/themes';
import { Button, Popover, Stack, Tooltip, TooltipPopover } from 'src/core/ui';
import { useBulkEditHub } from 'src/hooks/useBulkEditHub';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { LoadingIcon, WarningOutlineIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import {
  ActionOutboxEntityType,
  BulkActionType,
  Feature,
  Permission,
} from 'src/WebApiController';

import { SECTION_MAPPING_BULK_UPDATE_KEY } from './SectionMappingControl.constants';
import * as styles from './SectionMappingControl.css';
import {
  useApplySectionMapping,
  useSectionMappingBulk,
} from './SectionMappingControl.hooks';
import { SectionMappingControlProps } from './SectionMappingControl.types';

/**
 * The non-interactive unmapped-section indicator. Rendered as-is when
 * self-service section mapping is unavailable (feature off, no permission, or
 * no mapped event), preserving the pre-existing behavior.
 */
const UnmappedSectionWarning = () => (
  <TooltipPopover
    variant="link"
    contentVariant="dark"
    triggerContent={
      <WarningOutlineIcon fill={'#FD9927'} size={vars.iconSize.m} />
    }
  >
    <Content id={ContentId.SeatingInfoNotMatchStubHub} />
  </TooltipPopover>
);

/**
 * Self-service section mapping trigger for an unmapped listing (POS-9305).
 * Surfaces a red exclamation trigger that opens a popover with a StubHub
 * section dropdown; selecting a section and applying maps the listing's
 * StubHub marketplace seating override to it.
 */
export const SectionMappingControl = ({
  listing,
}: SectionMappingControlProps) => {
  const hasFeature = useUserHasFeature(Feature.CatalogSupport);
  const hasPermission = useUserHasAnyOfPermissions(
    Permission.Inventory_UpdateListingInfo,
    Permission.Inventory_UpdateListingInfoOnIsPricerOf,
    Permission.Inventory_UpdateListingInfoOnPurchasedBy
  );

  if (!hasFeature || !hasPermission || listing.viagEvId == null) {
    return <UnmappedSectionWarning />;
  }

  return <SectionMapper listing={listing} />;
};

const SectionMapper = ({ listing }: SectionMappingControlProps) => {
  const [open, setOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedSectionId, setSelectedSectionId] = useState<string>();
  const [applyToAll, setApplyToAll] = useState(false);

  // Keep the venue map / bulk preview loaded while either surface is active.
  const isActive = open || previewOpen;
  const venueMapQuery = useGetEventMapConfig(listing.viagEvId, !isActive);
  const sections = venueMapQuery.data?.sections;
  const hasNoSections =
    venueMapQuery.isSuccess && (sections?.length ?? 0) === 0;
  const selectedSection = sections?.find(
    (s) => s.id.toString() === selectedSectionId
  );

  const { applyMapping, isApplying } = useApplySectionMapping(listing);
  const bulk = useSectionMappingBulk(
    listing,
    venueMapQuery.data?.venueCfgId,
    isActive
  );
  const triggerLabel = useContent(ContentId.ChooseSection);

  // The bulk endpoint requires broadcast permission (Listing_BroadcastUnbroadcast),
  // whereas the single-listing apply only needs Inventory_UpdateListingInfo — gate
  // the bulk option separately so a user without broadcast rights isn't offered a
  // bulk apply that would 403.
  const hasBroadcastPermission = useUserHasAnyOfPermissions(
    Permission.Inventory_BroadcastUnbroadcast,
    Permission.Inventory_BroadcastUnbroadcastOnPurchasedBy,
    Permission.Inventory_BroadcastUnbroadcastOnIsPricerOf
  );

  // Only surface the bulk option when the user can broadcast and there is more
  // than the source listing.
  const canBulkApply = hasBroadcastPermission && (bulk.affectedCount ?? 0) > 1;
  const isBusy = isApplying || bulk.isApplying;

  // Bulk apply runs through the SignalR bulk-edit hub so large operations show
  // real progress; it falls back to a synchronous apply (with a spinner) when
  // the hub isn't connected.
  const [bulkSubmitted, setBulkSubmitted] = useState(false);
  const updateKey = `${SECTION_MAPPING_BULK_UPDATE_KEY}-${listing.id}`;
  const { setMainDialogOpened, setProgress, setPreview, setStage } =
    useBulkEditHubContext();

  const closeAll = useCallback(() => {
    setPreviewOpen(false);
    setOpen(false);
    setSelectedSectionId(undefined);
    setApplyToAll(false);
    setBulkSubmitted(false);
    setMainDialogOpened(false);
    setProgress(undefined);
    setPreview(undefined);
    setStage(BulkEditStage.Idle);
  }, [setMainDialogOpened, setPreview, setProgress, setStage]);

  const bulkRefresh = bulk.refresh;
  const onBulkEditDone = useCallback(async () => {
    await bulkRefresh();
    closeAll();
  }, [bulkRefresh, closeAll]);

  const { bulkEditHub, initJob, progress } = useBulkEditHub(
    ActionOutboxEntityType.Listing,
    BulkActionType.UpdateMarketplaceSeating,
    updateKey,
    onBulkEditDone
  );
  const supportsBackground =
    bulkEditHub?.state === HubConnectionState.Connected;
  const isBulkRunning = bulk.isApplying || bulkSubmitted || progress != null;

  // Applying to all first opens a preview of the affected listings; a single
  // listing applies immediately.
  const onApply = async () => {
    if (!selectedSection || isBusy) {
      return;
    }
    if (applyToAll && canBulkApply) {
      setPreviewOpen(true);
      return;
    }
    if (await applyMapping(selectedSection)) {
      closeAll();
    }
  };

  const onConfirmBulk = async () => {
    if (!selectedSection || isBulkRunning) {
      return;
    }
    setBulkSubmitted(true);
    const ok = await bulk.bulkApply(
      selectedSection,
      updateKey,
      supportsBackground,
      // Remap each listing's row into the new section (match, else speculative).
      true
    );
    if (!ok) {
      // Allow a retry if the request failed outright.
      setBulkSubmitted(false);
      return;
    }
    // Synchronous mode finishes here; background mode closes when the hub
    // reports completion (onBulkEditDone).
    if (!supportsBackground) {
      closeAll();
    }
  };

  return (
    <>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Tooltip
          contentVariant="dark"
          triggerContent={
            <Popover.Trigger asChild>
              <button
                type="button"
                className={styles.triggerButton}
                aria-label={triggerLabel}
              >
                <WarningOutlineIcon
                  fill={vars.color.textError}
                  size={vars.iconSize.m}
                  withHoverEffect
                />
              </button>
            </Popover.Trigger>
          }
        >
          <Content id={ContentId.SeatingInfoNotMatchStubHub} />
        </Tooltip>
        <Popover.Content align="end" className={styles.panel}>
          <Stack direction="column" gap="m">
            <span className={shared.typography.title6}>
              <Content id={ContentId.ChooseSection} />
            </span>
            {hasNoSections ? (
              <span className={styles.emptyMessage}>
                <Content id={ContentId.SeatingInfoNotMatchStubHub} />
              </span>
            ) : (
              <SectionSelector
                showFullWidth
                sections={sections}
                value={selectedSectionId ?? '-1'}
                placeholderText={ContentId.SearchOrEnter}
                loading={venueMapQuery.isLoading}
                onChange={(value) => setSelectedSectionId(value)}
              />
            )}
            {canBulkApply && (
              <Checkbox
                checked={applyToAll}
                disabled={isBusy}
                labelPosition="right"
                isV2Variant
                onChange={(e) => setApplyToAll(e.target.checked)}
                label={
                  <span>
                    <Content id={ContentId.SectionMappingApplyToVenueConfig} />{' '}
                    ({bulk.affectedCount})
                  </span>
                }
              />
            )}
            <Stack direction="row" gap="m" justifyContent="end">
              <Popover.Close asChild>
                <Button variant="outline">
                  <Content id={ContentId.Cancel} />
                </Button>
              </Popover.Close>
              <Button
                variant="regular"
                disabled={!selectedSectionId || isBusy}
                onClick={onApply}
              >
                {isApplying ? (
                  <LoadingIcon
                    className={styles.spinner}
                    size={vars.iconSize.m}
                  />
                ) : (
                  <Content id={ContentId.Apply} />
                )}
              </Button>
            </Stack>
          </Stack>
        </Popover.Content>
      </Popover.Root>
      {previewOpen && (
        <GenericDialog
          isOpen={previewOpen}
          size="xl"
          centered
          onOpened={initJob}
          onCancel={isBulkRunning ? undefined : closeAll}
          header={<Content id={ContentId.Preview} />}
          footer={
            <Stack direction="row" gap="m" justifyContent="end">
              <Button
                variant="outline"
                disabled={isBulkRunning}
                onClick={closeAll}
              >
                <Content id={ContentId.Cancel} />
              </Button>
              <Button
                variant="regular"
                disabled={!selectedSection || isBulkRunning}
                onClick={onConfirmBulk}
              >
                {isBulkRunning ? (
                  <LoadingIcon
                    className={styles.spinner}
                    size={vars.iconSize.m}
                  />
                ) : (
                  <>
                    <Content id={ContentId.Confirm} /> ({bulk.affectedCount})
                  </>
                )}
              </Button>
            </Stack>
          }
        >
          <div className={styles.previewDialogBody}>
            <BulkEditStatus
              entityType={ActionOutboxEntityType.Listing}
              updateKey={updateKey}
              isLoading={bulk.isApplying}
            >
              <BulkEditPreviewListingDetails preview={bulk.preview} />
            </BulkEditStatus>
          </div>
        </GenericDialog>
      )}
    </>
  );
};
